import { useEffect, useMemo } from "react";
import { useDesign } from "../../lib/store";
import { effectiveFinishes, wallFinishFor } from "../../lib/finishes/effective";
import { finishLabel, wallFinish } from "../../lib/finishes/catalog";
import { requestFinishes } from "../../lib/finishesRequests";
import { reportFinishesEntryShown } from "../../lib/finishesTelemetry";
import { ROOM_FINISHES_FLAG, useFlag } from "../../lib/flags";
import { cn } from "../../lib/cn";
import { FinishArt } from "./FinishTile";

/**
 * The Walls header's door to finishes: the active wall's swatch (and, on
 * desktop, "Paint · Warm white"). Opens the sheet on that wall.
 */
export function WallFinishChip({ wallKey, className }: { wallKey: string; className?: string }) {
  const enabled = useFlag(ROOM_FINISHES_FLAG);
  const room = useDesign((s) => s.design.room);
  const furniture = useDesign((s) => s.design.furniture);
  const finish = useMemo(() => wallFinishFor(effectiveFinishes({ room, furniture }), wallKey), [room, furniture, wallKey]);

  useEffect(() => {
    if (enabled) reportFinishesEntryShown("walls", "wall");
  }, [enabled]);

  if (!enabled) return null;
  const option = wallFinish(finish.type);
  const label = finishLabel(option, finish.color);
  return (
    <button
      type="button"
      onClick={() => requestFinishes({ source: "walls", view: "wall", surface: "walls", wallKey })}
      aria-label={`Wall finish: ${label}. Change it`}
      title="Change this wall's finish"
      data-testid="wall-finish-chip"
      className={cn(
        "group shrink-0 inline-flex h-8 items-center gap-1.5 rounded-full p-1 text-caption font-medium text-ink-700 ring-1 ring-ink-200 transition-colors hover:bg-ink-50 hover:ring-ink-300 active:bg-ink-100 md:pr-2.5 pointer-coarse:h-11 pointer-coarse:px-2",
        className
      )}
    >
      <FinishArt
        option={option}
        color={finish.color}
        className="relative size-6 shrink-0 rounded-full ring-1 ring-black/10 pointer-coarse:size-7"
      />
      <span className="hidden max-w-[11rem] truncate md:inline">{label}</span>
    </button>
  );
}
